import type { Detection } from '@mediapipe/tasks-vision';
import { ClientError } from './central-api/base-service';
import { facialLoginService } from './central-api/facial-login';
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

const FRAME_LONG_EDGE = 1280;
const FRAME_SHORT_EDGE = 720;
const PORTRAIT_ASPECT_RATIO = 3 / 4;
const LOCAL_INTERVAL = 200;
const CAPTURE_TIMEOUT_MS = 3000;
const JPEG_FALLBACK_MS = 250;
const JPEG_QUALITY = 0.95;
const LOCAL_DETECTION_THRESHOLD = Number(process.env.FACIAL_LOCAL_DETECTION_THRESHOLD || 0.6);
// Face framing targets as a fraction of the guide ellipse height.
const FAR_HALF_HEIGHT_THRESHOLD = 0.45;
const CLOSE_HALF_HEIGHT_THRESHOLD = 0.95;
// Consecutive aligned frames required before capturing, to tolerate micro-motion.
const ALIGNED_HOLD_FRAMES = 2;

export type FacialAlignReason = 'none' | 'many' | 'far' | 'close' | 'off' | 'aligned';
export type FacialAlignDirection = 'top' | 'right' | 'bottom' | 'left';
export type FacialCaptureState = {
    key: string;
    aligned: boolean;
    reason: FacialAlignReason;
    direction?: FacialAlignDirection;
};
type Callbacks = {
    onState: (state: FacialCaptureState) => void;
    onVerified: () => Promise<void>;
    onError: (error: unknown) => void;
};

function bounded<T>(promise: Promise<T>, milliseconds: number, key: string): Promise<T> {
    let timer: ReturnType<typeof setTimeout>;
    return Promise.race([
        promise,
        new Promise<never>((_resolve, reject) => {
            timer = setTimeout(() => {
                reject(new ClientError(key));
            }, milliseconds);
        }),
    ]).finally(() => {
        clearTimeout(timer);
    });
}

export function frameJPEG(canvas: HTMLCanvasElement, signal: AbortSignal): Promise<Blob> {
    return new Promise((resolve, reject) => {
        let completed = false;
        let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
        function finish(blob: Blob | null, error?: unknown) {
            if (completed) {
                return;
            }
            completed = true;
            if (fallbackTimer !== null) {
                clearTimeout(fallbackTimer);
            }
            signal.removeEventListener('abort', abort);
            if (error || !blob) {
                reject(error || new ClientError('facialLoginInvalidImage'));
            } else {
                resolve(blob);
            }
        }
        function abort() {
            finish(null, new DOMException('Aborted', 'AbortError'));
        }
        function fallback() {
            if (completed) {
                return;
            }
            try {
                const prefix = 'data:image/jpeg;base64,';
                const encoded = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
                if (!encoded.startsWith(prefix)) {
                    throw new ClientError('facialLoginInvalidImage');
                }
                const binary = atob(encoded.slice(prefix.length));
                const bytes = new Uint8Array(binary.length);
                for (let index = 0; index < binary.length; index += 1) {
                    bytes[index] = binary.charCodeAt(index);
                }
                finish(new Blob([bytes], { type: 'image/jpeg' }));
            } catch (error) {
                finish(null, error);
            }
        }
        signal.addEventListener('abort', abort, { once: true });
        if (signal.aborted) {
            abort();
            return;
        }
        fallbackTimer = setTimeout(fallback, JPEG_FALLBACK_MS);
        try {
            canvas.toBlob((blob) => {
                if (blob) {
                    finish(blob);
                } else {
                    fallback();
                }
            }, 'image/jpeg', JPEG_QUALITY);
        } catch {
            fallback();
        }
    });
}

export function analyzeFaceGuide(detection: Detection, canvas: HTMLCanvasElement, preview: DOMRect, guide: DOMRect, source = {
    width: canvas.width, height: canvas.height,
    cropX: 0, cropY: 0, cropWidth: canvas.width, cropHeight: canvas.height,
}): { aligned: boolean; dx: number; dy: number; halfWidth: number; halfHeight: number; } | null {
    const box = detection.boundingBox;
    if (!box || ![box.originX, box.originY, box.width, box.height].every(Number.isFinite)
        || box.width <= 0 || box.height <= 0 || !preview.width || !preview.height || !guide.width || !guide.height) {
        return null;
    }
    // Map the cropped snapshot to the full, mirrored video shown with object-fit: contain.
    const scale = Math.min(preview.width / source.width, preview.height / source.height);
    const offsetX = (preview.width - source.width * scale) / 2;
    const offsetY = (preview.height - source.height * scale) / 2;
    const sourceScaleX = source.cropWidth / canvas.width;
    const sourceScaleY = source.cropHeight / canvas.height;
    const centerX = preview.left + preview.width - (offsetX + (source.cropX + (box.originX + box.width / 2) * sourceScaleX) * scale);
    const centerY = preview.top + offsetY + (source.cropY + (box.originY + box.height / 2) * sourceScaleY) * scale;
    const dx = (centerX - guide.left - guide.width / 2) / (guide.width / 2);
    const dy = (centerY - guide.top - guide.height / 2) / (guide.height / 2);
    const halfWidth = box.width * sourceScaleX * scale / guide.width;
    const halfHeight = box.height * sourceScaleY * scale / guide.height;
    let aligned = dx * dx + dy * dy <= 0.65 ** 2;
    if (aligned) {
        for (let point = 0; point < 64; point += 1) {
            const angle = point * 2 * Math.PI / 64;
            const x = dx + halfWidth * Math.cos(angle);
            const y = dy + halfHeight * Math.sin(angle);
            if (x * x + y * y > 1) {
                aligned = false;
                break;
            }
        }
    }
    return { aligned, dx, dy, halfWidth, halfHeight };
}

// Arc on the oval border that the user should move toward.
function directionTowardCenter(dx: number, dy: number): FacialAlignDirection {
    return Math.abs(dx) > Math.abs(dy)
        ? (dx > 0 ? 'left' : 'right')
        : (dy > 0 ? 'top' : 'bottom');
}

export class FacialCamera {
    private stream: MediaStream | null = null;
    private detector: FaceDetector | null = null;
    private generation = 0;
    private timer?: ReturnType<typeof setTimeout>;
    private controller?: AbortController;
    private disposed = false;
    private aligned = false;
    private reason: FacialAlignReason = 'none';
    private alignedCount = 0;

    constructor(private video: HTMLVideoElement, private guide: HTMLElement, private callbacks: Callbacks) { }

    private update(key: string, reason: FacialAlignReason = 'none', direction?: FacialAlignDirection) {
        this.reason = reason;
        this.aligned = reason === 'aligned';
        if (!this.disposed) {
            this.callbacks.onState({ key, aligned: this.aligned, reason, direction });
        }
    }

    private async prepareDetector() {
        if (this.detector) {
            return;
        }
        try {
            if (!Number.isFinite(LOCAL_DETECTION_THRESHOLD) || LOCAL_DETECTION_THRESHOLD <= 0 || LOCAL_DETECTION_THRESHOLD > 1) {
                throw new Error('Invalid local detection threshold');
            }
            const root = new URL('/facial-detector/0.10.32/', window.location.origin).href;
            const files = await FilesetResolver.forVisionTasks(root.replace(/\/$/, ''));
            const detector = await FaceDetector.createFromOptions(files, {
                baseOptions: { modelAssetPath: root + 'blaze_face_short_range.tflite', delegate: 'CPU' },
                runningMode: 'IMAGE',
                minDetectionConfidence: LOCAL_DETECTION_THRESHOLD,
            });
            if (this.disposed) {
                detector.close();
                return;
            }
            this.detector = detector;
        } catch {
            throw new ClientError('facialLoginUnavailable');
        }
    }

    async start() {
        this.pauseCapture();
        const token = this.generation;
        if (this.disposed) {
            return;
        }
        this.controller = new AbortController();
        this.update(this.stream?.getVideoTracks().some(track => track.readyState === 'live')
            ? 'facialLoginNoFace' : 'facialLoginPreparing');
        try {
            if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
                throw new ClientError('facialLoginSecureConnection');
            }
            await this.prepareDetector();
            if (token !== this.generation) {
                return;
            }
            if (!this.stream?.getVideoTracks().some(track => track.readyState === 'live')) {
                this.stream?.getTracks().forEach(track => {
                    track.stop();
                });
                this.stream = null;
                this.update('facialLoginCameraPermission');
                const preview = this.video.getBoundingClientRect();
                const portrait = preview.height > preview.width;
                const captured = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: { ideal: 'user' },
                        width: { ideal: portrait ? FRAME_SHORT_EDGE : FRAME_LONG_EDGE },
                        height: { ideal: portrait ? FRAME_LONG_EDGE : FRAME_SHORT_EDGE },
                        resizeMode: { ideal: 'none' },
                    } as MediaTrackConstraints,
                });
                if (token !== this.generation) {
                    captured.getTracks().forEach((track) => {
                        track.stop();
                    });
                    return;
                }
                this.stream = captured;
                this.video.srcObject = captured;
                this.update('facialLoginCameraStarting');
            }
            await this.video.play();
            if (token !== this.generation) {
                return;
            }
            await this.sendFrame(token);
        } catch (error) {
            if (token === this.generation) {
                this.fail(error);
            }
        }
    }

    private schedule(token: number) {
        this.timer = setTimeout(() => {
            this.sendFrame(token).catch((error) => {
                if (token === this.generation) {
                    this.fail(error);
                }
            });
        }, LOCAL_INTERVAL);
    }

    private async sendFrame(token: number) {
        if (token !== this.generation || !this.stream || !this.detector) {
            return;
        }
        const canvas = document.createElement('canvas');
        const width = this.video.videoWidth;
        const height = this.video.videoHeight;
        const portrait = width < height;
        const cropWidth = portrait ? Math.min(width, height * PORTRAIT_ASPECT_RATIO) : width;
        const cropHeight = portrait ? Math.min(height, width / PORTRAIT_ASPECT_RATIO) : height;
        const source = {
            width, height, cropWidth, cropHeight,
            cropX: (width - cropWidth) / 2,
            cropY: (height - cropHeight) / 2,
        };
        const scale = Math.min(1, FRAME_LONG_EDGE / Math.max(cropWidth, cropHeight), FRAME_SHORT_EDGE / Math.min(cropWidth, cropHeight));
        canvas.width = Math.max(1, Math.round(cropWidth * scale));
        canvas.height = Math.max(1, Math.round(cropHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) {
            throw new ClientError('facialLoginInvalidImage');
        }
        context.drawImage(this.video, source.cropX, source.cropY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height);
        const { detections } = this.detector.detect(canvas);

        if (detections.length === 0) {
            this.alignedCount = 0;
            this.update('facialLoginNoFace', 'none');
            this.schedule(token);
            return;
        }
        if (detections.length > 1) {
            this.alignedCount = 0;
            this.update('facialLoginOnePerson', 'many');
            this.schedule(token);
            return;
        }
        const analysis = analyzeFaceGuide(detections[0], canvas, this.video.getBoundingClientRect(), this.guide.getBoundingClientRect(), source);
        if (!analysis) {
            this.alignedCount = 0;
            this.update('facialLoginNoFace', 'none');
            this.schedule(token);
            return;
        }
        if (analysis.halfHeight < FAR_HALF_HEIGHT_THRESHOLD) {
            this.alignedCount = 0;
            this.update('facialLoginTooFar', 'far');
            this.schedule(token);
            return;
        }
        if (analysis.halfHeight > CLOSE_HALF_HEIGHT_THRESHOLD) {
            this.alignedCount = 0;
            this.update('facialLoginTooClose', 'close');
            this.schedule(token);
            return;
        }
        if (!analysis.aligned) {
            this.alignedCount = 0;
            this.update('facialLoginOffCenter', 'off', directionTowardCenter(analysis.dx, analysis.dy));
            this.schedule(token);
            return;
        }

        this.alignedCount += 1;
        if (this.alignedCount < ALIGNED_HOLD_FRAMES) {
            this.update('facialLoginHold', 'aligned');
            this.schedule(token);
            return;
        }

        this.update('facialLoginEncoding', 'aligned');
        // Encode the exact HD snapshot that passed local detection and framing.
        const blob = await bounded(frameJPEG(canvas, this.controller!.signal), CAPTURE_TIMEOUT_MS, 'facialLoginCaptureTimeout');
        if (token !== this.generation) {
            return;
        }
        this.update('facialLoginWaitingResponse', 'aligned');
        await facialLoginService.validateFacialLogin(blob, this.controller!.signal);
        if (token !== this.generation) {
            return;
        }
        this.stopCamera();
        await this.callbacks.onVerified();
    }

    private pauseCapture() {
        this.generation += 1;
        this.controller?.abort();
        clearTimeout(this.timer);
        this.aligned = false;
        this.reason = 'none';
        this.alignedCount = 0;
    }

    private stopCamera() {
        this.pauseCapture();
        this.stream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.stream = null;
        this.video.srcObject = null;
    }

    cancel(): void {
        this.stopCamera();
    }

    private fail(error: unknown) {
        this.pauseCapture();
        if (!this.disposed) {
            const cameraErrors: Record<string, string> = {
                NotAllowedError: 'facialLoginCameraDenied', NotFoundError: 'facialLoginCameraMissing', NotReadableError: 'facialLoginCameraBusy',
            };
            const name = error instanceof Error ? error.name : '';
            this.callbacks.onError(cameraErrors[name] ? new ClientError(cameraErrors[name]) : error);
        }
    }

    dispose() {
        this.disposed = true;
        this.cancel();
        this.detector?.close();
        this.detector = null;
    }
}
